// API v1 contracts shared by server and client. The server validates every request with these
// schemas; the client uses the same types. Paths are frozen here as they are implemented.
import { z } from 'zod';
import { BOOKING_POLICY_VALUES, type BookingPolicyValue } from './recurrence.ts'; // PB-12-03

export const API_PREFIX = '/api/v1';

// ------------------------------------------------------------------ common
export const Uuid = z.uuid();
export const IdempotencyKey = z.string().regex(/^[A-Za-z0-9_-]{16,128}$/, 'api.idempotencyInvalid');
// Messages are @passage/i18n catalogue keys (PD-015); the API translates them per request.
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const optionalText = (max: number) => z.string().trim().max(max, 'v.tooLong').nullable().optional();

export type ErrorCode =
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION_FAILED'
  | 'VERSION_CONFLICT'
  | 'IDEMPOTENCY_KEY_REUSED'
  | 'IDEMPOTENCY_KEY_REQUIRED'
  | 'CONFLICT'
  | 'LAST_ADMINISTRATOR'
  | 'IMPACT_REVIEW_REQUIRED'
  // PB-12-07 / PD-066: a site-bound action named a different working site than the resource's.
  | 'SITE_CONTEXT_MISMATCH'
  | 'ENTITLEMENT_REQUIRED'
  | 'INVALID_CREDENTIALS'
  | 'RATE_LIMITED'
  | 'CSRF_REJECTED'
  | 'ORIGIN_REJECTED'
  | 'UNKNOWN_ORGANISATION'
  | 'POLICY_DECISION_PENDING'
  | 'UNSUPPORTED_MEDIA_TYPE'
  | 'SITE_LIMIT_REACHED'
  | 'PAYLOAD_TOO_LARGE'
  | 'ROOM_CONFLICT'
  | 'CAPACITY_EXCEEDED'
  // PB-12-03 (PD-477): over capacity of a 'warn' destination; repeat with acknowledgeRoomWarning.
  | 'ROOM_WARNING'
  | 'SUBMISSION_INCOMPLETE'
  | 'SCHEDULE_INVALID'
  | 'SSO_FAILED'
  | 'SSO_NOT_LINKED'
  | 'TEST_REQUIRED'
  | 'EVALUATION_STALE'
  | 'ARRIVAL_BLOCKED'
  // ADR-020 (privacy & integrations stream)
  | 'RETENTION_UNCONFIGURED'
  | 'FOUR_EYES_REQUIRED'
  | 'SUBJECT_MATCH_REFUSED'
  | 'REQUESTER_NOT_VERIFIED'
  | 'DESTINATION_BLOCKED'
  | 'UNSUPPORTED_OPERATION'
  // PD-714 / PD-725 (demoguard stream): demo mode (IS_DEMO) — the change was not saved or sent.
  | 'DEMO_READ_ONLY'
  // PD-775 (demosandbox stream): demo mode — another visitor of the demo is trying the same record now.
  | 'DEMO_SANDBOX_BUSY'
  | 'INTERNAL';

export interface ApiErrorBody {
  readonly error: {
    readonly code: ErrorCode;
    readonly message: string;
    readonly fields?: Readonly<Record<string, string>>;
    readonly details?: Readonly<Record<string, unknown>>;
    readonly correlationId: string;
  };
}

// ------------------------------------------------------------------ auth
export const LoginRequest = z.object({
  email: z.string().trim().min(3).max(254),
  password: z.string().min(1).max(1024),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

export interface AuthContextResponse {
  /** Which surface this host serves: an organisation, the system console (PD-003) or nothing. */
  readonly surface: 'organisation' | 'platform' | 'unknown';
  readonly organisation: { readonly name: string } | null;
  readonly methods: { readonly localPassword: boolean; readonly sso: boolean };
  /** ADR-011: active identity providers offered on the sign-in page (names only). */
  readonly ssoProviders?: ReadonlyArray<{ readonly id: string; readonly name: string; readonly protocol: 'oidc' | 'saml' }>;
  /** PD-714 / PD-729: present and true only in demo mode (IS_DEMO): every surface shows the demo banner. */
  readonly demo?: true;
}

export interface SessionSite {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly timeZone: string;
  readonly status: 'active' | 'inactive';
}

export interface SessionResponse {
  readonly user: { readonly id: string; readonly displayName: string; readonly email: string; readonly locale: 'en' | 'ar' };
  readonly organisation: { readonly id: string; readonly name: string };
  /** Experiences granted to the user. Navigation only — the API enforces every action. */
  readonly experiences: ReadonlyArray<{ readonly id: string; readonly siteId: string | null }>;
  readonly access: { readonly tenant: readonly string[]; readonly sites: Readonly<Record<string, readonly string[]>> };
  readonly sites: readonly SessionSite[];
  readonly entitlements: readonly string[];
  readonly accessVersion: number;
  readonly csrfToken: string;
  readonly expiresAt: string;
  /** Policy values the client needs for forms (PD-006, PD-010). */
  readonly policy: { readonly siteGrantMaxDays: number; readonly passwordMinLength: number; readonly mfaRequired: boolean };
  readonly mfa: { readonly enrolled: boolean };
  /** OR-10 (PD-056): organisation date and time display format. */
  readonly display: { readonly dateStyle: 'medium' | 'long' | 'numeric' | 'iso'; readonly hourCycle: 'locale' | 'h23' | 'h12' };
  /** PD-714 / PD-729: present and true only in demo mode (IS_DEMO). */
  readonly demo?: true;
}

// ------------------------------------------------------------------ multi-factor authentication (PB-17-08, PD-020)
/** Returned by sign-in instead of a session when a second step is needed. */
export interface MfaChallengeResponse {
  readonly next: 'mfa' | 'mfa_enrol';
  readonly challengeToken: string;
  readonly expiresAt: string;
}
export const MfaVerifyRequest = z.object({
  challengeToken: z.string().min(20).max(200),
  code: z.string().trim().max(20).optional(),
  recoveryCode: z.string().trim().max(40).optional(),
});
export const MfaEnrolStartRequest = z.object({ challengeToken: z.string().min(20).max(200).optional() });
export interface MfaEnrolStartResponse {
  readonly otpauthUri: string;
  /** Base32 secret for manual entry. Shown once, only during enrolment. */
  readonly secret: string;
}
export const MfaEnrolConfirmRequest = z.object({ challengeToken: z.string().min(20).max(200).optional(), code: z.string().trim().regex(/^\d{6}$/, 'v.otp') });
export interface MfaRecoveryCodesResponse {
  /** One-time recovery codes, shown once. */
  readonly recoveryCodes: readonly string[];
}
export const MfaDisableRequest = z.object({ code: z.string().trim().regex(/^\d{6}$/, 'v.otp') });

// ------------------------------------------------------------------ sites
const TimeZone = z.string().trim().min(1).max(64);
const OperatingHours = z
  .array(
    z.object({
      day: z.number().int().min(1).max(7),
      open: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'v.time'),
      close: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'v.time'),
    }),
  )
  .max(28);

export const CreateSiteRequest = z.object({
  code: z.string().trim().toUpperCase().regex(/^[A-Z0-9][A-Z0-9-]{0,31}$/, 'v.siteCode'),
  name: trimmed(200),
  timeZone: TimeZone,
  countryCode: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/, 'v.countryCode').nullable().optional(),
  address: optionalText(1000),
  contactEmail: z.email('v.email').max(254, 'v.tooLong').nullable().optional(),
  contactPhone: optionalText(40),
  visitorInstructions: optionalText(4000),
  operatingHours: OperatingHours.optional(),
});
export type CreateSiteRequest = z.infer<typeof CreateSiteRequest>;

export const UpdateSiteRequest = CreateSiteRequest.omit({ code: true })
  .partial()
  .extend({
    expectedVersion: z.number().int().min(1),
    /** Required when timeZone changes: future schedules keep their UTC instants (PB-12-10, PB-12-01). */
    timeZoneChangeAcknowledged: z.boolean().optional(),
    /** ADR-029: token of the reviewed future-visit impact; required when future visits exist. */
    timeZoneImpactToken: z.string().max(64).optional(),
    /** ADR-029: token of the reviewed hours impact; required when future visits would fall outside. */
    operatingHoursImpactToken: z.string().max(64).optional(),
  });
export type UpdateSiteRequest = z.infer<typeof UpdateSiteRequest>;

export interface SiteResponse {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly timeZone: string;
  readonly countryCode: string | null;
  readonly address: string | null;
  readonly contactEmail: string | null;
  readonly contactPhone: string | null;
  readonly visitorInstructions: string | null;
  readonly operatingHours: ReadonlyArray<{ day: number; open: string; close: string }>;
  readonly status: 'active' | 'inactive';
  readonly version: number;
  readonly updatedAt: string;
  readonly localNow: { readonly date: string; readonly time: string; readonly utcOffset: string };
}

// ------------------------------------------------------------------ locations
export const LOCATION_KIND_VALUES = ['campus', 'building', 'floor', 'zone', 'gate', 'room', 'desk', 'parking', 'other'] as const;

export const CreateLocationRequest = z.object({
  kind: z.enum(LOCATION_KIND_VALUES),
  name: trimmed(200),
  code: z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/, 'v.locationCode').nullable().optional(),
  capacity: z.number().int().min(1).max(100000).nullable().optional(),
  parentId: Uuid.nullable().optional(),
  /** PB-12-03 (PD-476): omitted = rooms block, other kinds are not booked. */
  bookingPolicy: z.enum(BOOKING_POLICY_VALUES).optional(),
});
export type CreateLocationRequest = z.infer<typeof CreateLocationRequest>;

export const UpdateLocationRequest = CreateLocationRequest.omit({ kind: true })
  .partial()
  .extend({ expectedVersion: z.number().int().min(1) });
export type UpdateLocationRequest = z.infer<typeof UpdateLocationRequest>;

export const LocationStatusChangeRequest = z.object({
  expectedVersion: z.number().int().min(1),
  reason: trimmed(500),
  /** PB-12-04: the fingerprint from the impact review; a concurrent booking changes it. */
  impactToken: z.string().max(100).optional(),
  /** Acknowledges listed rules that stop matching (approver scopes, arrival guidance). */
  confirmImpact: z.boolean().optional(),
});
export type LocationStatusChangeRequest = z.infer<typeof LocationStatusChangeRequest>;

export interface LocationResponse {
  readonly id: string;
  readonly siteId: string;
  readonly parentId: string | null;
  readonly kind: (typeof LOCATION_KIND_VALUES)[number];
  readonly name: string;
  readonly code: string | null;
  readonly capacity: number | null;
  /** PB-12-03: block (no overlap, capacity enforced) / warn (acknowledged) / none (not booked). */
  readonly bookingPolicy: BookingPolicyValue;
  readonly status: 'active' | 'inactive';
  readonly version: number;
  readonly updatedAt: string;
  /** Protected picture, downloadable through /api/v1/objects/:id when the caller may read the site. */
  readonly pictureObjectId: string | null;
}

export interface LocationListResponse {
  readonly siteId: string;
  readonly items: readonly LocationResponse[];
  readonly truncated: boolean;
}

export interface DeactivationImpact {
  readonly locationId: string;
  readonly activeChildren: ReadonlyArray<{ readonly id: string; readonly name: string; readonly kind: string }>;
  /** PB-12-04 (PD-064): planned visits that still use this location as destination (must be moved first). */
  readonly futureVisits: { readonly count: number; readonly sample: ReadonlyArray<{ readonly id: string; readonly reference: string; readonly startsAt: string | null }> };
  /** Active access-control zone mappings (module 11) that must be disabled first. */
  readonly zoneMappings: number;
  /** Rules that stop matching once it is inactive; listed and confirmed, not blocking. */
  readonly policyReferences: ReadonlyArray<{ readonly kind: 'approver_group_member' | 'arrival_guidance'; readonly id: string; readonly label: string }>;
  /** Dependency sources not yet implemented are listed so nobody mistakes "0" for a checked fact. */
  readonly uncheckedDependencies: readonly string[];
  readonly blocked: boolean;
  /** Fingerprint of what this review saw; a different fingerprint at apply means something changed. */
  readonly impactToken: string;
}

// ------------------------------------------------------------------ branding (PB-12-09)
export const UpdateBrandingRequest = z.object({
  expectedVersion: z.number().int().min(0),
  primaryColour: z.string().trim().toLowerCase().regex(/^#[0-9a-f]{6}$/, 'v.colour').nullable(),
  contactLine: z.string().trim().max(200, 'v.tooLong').nullable(),
});
export interface BrandingResponse {
  readonly siteId: string;
  /** 0 when the site has no branding yet. */
  readonly version: number;
  readonly primaryColour: string | null;
  readonly contactLine: string | null;
  readonly logo: { readonly objectId: string; readonly contentType: string; readonly byteSize: number } | null;
  /** WCAG contrast of white text on the brand colour. */
  readonly contrastOnWhite: number | null;
}

// ------------------------------------------------------------------ users and grants
export const IssueGrantRequest = z.object({
  kind: z.enum(['experience', 'capability', 'specialist']),
  code: z.string().trim().min(2).max(64),
  siteId: Uuid.nullable(),
  validUntil: z.iso.datetime({ offset: true }).nullable().optional(),
  reason: trimmed(500),
});
export type IssueGrantRequest = z.infer<typeof IssueGrantRequest>;

export const RevokeGrantRequest = z.object({ reason: trimmed(500) });
export type RevokeGrantRequest = z.infer<typeof RevokeGrantRequest>;

export const DeactivateUserRequest = z.object({ expectedVersion: z.number().int().min(1), reason: trimmed(500) });
export type DeactivateUserRequest = z.infer<typeof DeactivateUserRequest>;

export const RevokeSessionsRequest = z.object({ reason: trimmed(500) });
export type RevokeSessionsRequest = z.infer<typeof RevokeSessionsRequest>;

export interface GrantResponse {
  readonly id: string;
  readonly kind: 'experience' | 'capability' | 'specialist';
  readonly code: string;
  readonly siteId: string | null;
  readonly validFrom: string;
  readonly validUntil: string | null;
  readonly revokedAt: string | null;
  readonly issuedReason: string;
  readonly provenance: string;
  readonly active: boolean;
}

export interface UserSummary {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly kind: 'user' | 'automation';
  readonly status: 'invited' | 'active' | 'disabled';
  readonly version: number;
  readonly experiences: ReadonlyArray<{ readonly id: string; readonly siteId: string | null }>;
  readonly specialistRoles: ReadonlyArray<{ readonly id: string; readonly siteId: string | null }>;
}

export interface UserListResponse {
  readonly items: readonly UserSummary[];
  readonly nextCursor: string | null;
}

export interface UserDetailResponse extends UserSummary {
  readonly accessVersion: number;
  readonly grants: readonly GrantResponse[];
  readonly effectiveAccess: { readonly tenant: readonly string[]; readonly sites: Readonly<Record<string, readonly string[]>> };
  /** null when the viewer may not see session information (site administrators, PD-014). */
  readonly activeSessionCount: number | null;
  /** True when grants outside the viewer's sites were withheld (PD-014). */
  readonly partialView: boolean;
}

// ------------------------------------------------------------------ security policy (PD-001, PD-002)
/** Bounds mirror the CHECK constraints in migration 0002. */
export const POLICY_BOUNDS = {
  sessionIdleMinutes: [5, 480],
  sessionAbsoluteHours: [1, 168],
  loginMaxFailures: [3, 20],
  loginFailureWindowMinutes: [1, 1440],
  loginLockMinutes: [1, 1440],
  networkMaxFailures: [5, 500],
  passwordMinLength: [12, 128],
  activationLinkHours: [1, 168],
  siteGrantMaxDays: [1, 365],
} as const;
export type PolicyField = keyof typeof POLICY_BOUNDS;
const bounded = (k: PolicyField) => z.number().int('v.invalid').min(POLICY_BOUNDS[k][0], 'v.range').max(POLICY_BOUNDS[k][1], 'v.range');
export const MFA_REQUIREMENTS = ['none', 'administrators', 'all'] as const;
export type MfaRequirement = (typeof MFA_REQUIREMENTS)[number];
export const SecurityPolicyFields = z.object({
  ...(Object.fromEntries(Object.keys(POLICY_BOUNDS).map((k) => [k, bounded(k as PolicyField)])) as { [K in PolicyField]: z.ZodNumber }),
  // PD-020: who must use multi-factor authentication.
  mfaRequirement: z.enum(MFA_REQUIREMENTS).default('administrators'),
});
export type SecurityPolicyFields = { readonly [K in PolicyField]: number } & { readonly mfaRequirement: MfaRequirement };
export const UpdateSecurityPolicyRequest = SecurityPolicyFields.extend({ expectedVersion: z.number().int().min(1).optional() });
export type UpdateSecurityPolicyRequest = z.infer<typeof UpdateSecurityPolicyRequest>;
export interface SecurityPolicyResponse extends SecurityPolicyFields {
  readonly source: 'platform' | 'organisation';
  readonly version: number;
}

// ------------------------------------------------------------------ onboarding (PD-009) and preferences (PD-011)
export const CreateUserRequest = z.object({
  email: z.email('v.email').max(254, 'v.tooLong'),
  displayName: trimmed(200),
});
export type CreateUserRequest = z.infer<typeof CreateUserRequest>;
export interface ActivationIssued {
  /** Shown once to the issuing administrator; never stored in clear. */
  readonly activationToken: string;
  readonly expiresAt: string;
}
export interface CreateUserResponse {
  readonly user: UserSummary;
  readonly activation: ActivationIssued;
}
export const ActivationInspectRequest = z.object({ token: z.string().min(20).max(200) });
export const ActivationCompleteRequest = z.object({ token: z.string().min(20).max(200), password: z.string().min(1, 'v.required').max(1024, 'v.tooLong') });
export interface ActivationInspectResponse {
  readonly email: string;
  readonly passwordMinLength: number;
}
export const UpdatePreferencesRequest = z.object({ locale: z.enum(['en', 'ar']) });

// ------------------------------------------------------------------ audit
export interface AuditEventResponse {
  readonly id: string;
  readonly occurredAt: string;
  readonly actorType: string;
  readonly actorUserId: string | null;
  /** Safety & reporting stream (PB-15-11): the actor's display name when the auditor may see it. */
  readonly actorName?: string | null;
  readonly siteId: string | null;
  readonly action: string;
  readonly resourceType: string;
  readonly resourceId: string | null;
  readonly outcome: 'succeeded' | 'denied' | 'failed';
  readonly reason: string | null;
  /** Present only with the explicit audit.read.sensitive capability. */
  readonly changes?: Record<string, unknown> | null;
  readonly correlationId: string | null;
}

export interface AuditListResponse {
  readonly items: readonly AuditEventResponse[];
  readonly nextCursor: string | null;
}

export const ListQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  after: z.string().max(200).optional(),
});
export * from './platform.ts';
export * from './visits.ts';
export * from './kiosk.ts';
export * from './communications.ts';
export * from './workflows.ts';

// Platform operations (module 19, ADR-012).
export * from './operations.ts';
export * from './identity.ts';
export * from './contractors.ts';
export * from './vehicles.ts';
export * from './people.ts';
export * from './safety.ts'; // safety & reporting stream (ADR-019)
// Privacy (module 16) and integrations (module 18), ADR-020.
export * from './privacy.ts';
export * from './integrations.ts';
export * from './verification.ts';
export * from './credentials.ts'; // ADR-018 (credentials stream)
export * from './calendars.ts'; // calendars stream (ADR-022)
export * from './adapters.ts'; // ADR-025 (adapters & edge stream)
export * from './reference-data.ts'; // OR-10 configuration dictionary
export * from './ai.ts'; // AI add-ons stream (ADR-024)
export * from './reception-gaps.ts'; // reception remainders (PB-06-04, PB-06-12, PB-04-07)
export * from './opsui.ts'; // operations-console stream (ADR-029)
export * from './visitor-workflows.ts'; // visitor-type workflows stream (ADR-027)
export * from './orgs.ts'; // people & organisations stream (ADR-028)
export * from './reception-lookup.ts'; // PB-06-02 sensitive lookup fields (PD-492, PD-493)
export * from './recurrence.ts'; // recurrence & rooms stream (PB-04-02, PB-12-03)
export * from './visit-series.ts'; // recurrence & rooms stream (PB-04-02)
export * from './model.ts'; // PB-18-07 departments and HR mapping, PB-12-05 site retention overrides
export * from './opsview.ts'; // operations views stream (PB-19-08, PB-19-09; PD-555 – PD-569)
export * from './portal.ts'; // portal stream: receipts, UAE PASS signing step, device badge printing (PD-510 – PD-515)
export * from './email.ts'; // PB-13-04 email delivery settings (email stream, PD-580 – PD-589)
export * from './priv.ts'; // privacy values stream: retroactive minimisation, publish confirmation, legal basis catalogue (PD-610 – PD-624)
export * from './malware.ts'; // clamav stream: explicit malware rescans (PD-651)
export * from './settings.ts'; // settings stream: installation settings registry (PD-710, PD-665 – PD-689)
export * from './demo.ts'; // demodata stream: demo sign-in help (PD-714, PD-744)
